// react
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from 'router';

// components
import { ChromeTemplate } from '@karehero/llama/pwa/templates/ChromeTemplate/ChromeTemplate';
import { type LinkItem } from '@karehero/llama/pwa/molecules/NavDrawer/NavDrawer';
import { type AccountInfoProps } from '@karehero/llama/pwa/atoms/AccountInfo/AccountInfo';

// hooks
import { useCareCircle } from 'hooks';

// utils
import { getProfilePictureUrl } from '@karehero/llama/pwa/utils/getProfilePictureUrl';

// posthog
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import useIntercom from 'hooks/useIntercom';

/**
 * RootChrome is the root layout for authenticated member pages.
 * Provides nav links, account info, and messenger intercom integration.
 */
export const RootChrome = () => {
  // hooks
  const { careCircle } = useCareCircle();
  const { flags } = useFeatureFlags();
  const navigate = useNavigate();
  const location = useLocation();
  const { showIntercom } = useIntercom();

  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    if (
      location.pathname === ROUTES.MESSENGER ||
      qs.get('messenger') === 'true'
    ) {
      showIntercom();
    }
  }, [location, showIntercom]);

  // memo
  const careRecipient = useMemo(() => {
    return careCircle?.careRecipientAccount;
  }, [careCircle]);

  const searchRoute = flags.careSearchDirectoryShortlist
    ? ROUTES.CARE_SEARCH
    : ROUTES.DIRECTORY;

  const links: LinkItem[] = useMemo(
    () => [
      {
        icon: 'house-blank',
        label: 'Home',
        to: ROUTES.HOME,
        onClick: () => navigate(ROUTES.HOME),
      },
      {
        icon: 'calendar',
        label: 'Actions',
        to: ROUTES.ORGANISER,
        onClick: () => navigate(ROUTES.ORGANISER),
      },
      {
        icon: 'hand-holding-heart',
        label: 'Profile',
        to: ROUTES.CARE_PROFILE,
        onClick: () => navigate(ROUTES.CARE_PROFILE),
      },
      {
        icon: 'magnifying-glass-location',
        label: 'Search',
        to: searchRoute,
        onClick: () => navigate(searchRoute),
      },
      {
        icon: 'message',
        label: 'Chat',
        onClick: showIntercom,
      },
    ],
    [navigate, showIntercom, searchRoute],
  );

  const navDrawerAccountInfoProps: AccountInfoProps = useMemo(
    () => ({
      name: `${careRecipient?.firstName || ''} ${
        careRecipient?.lastName || ''
      }`,
      role: 'Your Loved One',
      fallback: `${careRecipient?.firstName?.[0] || ''}${
        careRecipient?.lastName?.[0] || ''
      }`.toUpperCase(),
      avatarSrc: getProfilePictureUrl(careRecipient),
      isMenuDisabled: true,
      order: 'avatar-first' as const,
    }),
    [careRecipient],
  );

  return (
    <ChromeTemplate
      links={links}
      accountInfoProps={navDrawerAccountInfoProps}
      activePath={location.pathname}
    />
  );
};

RootChrome.displayName = 'RootChrome';
export default RootChrome;
